import { formatDate, formatTime } from '@/utils/format';

// Student incident reports (parent-records): shared by My Reports and
// Student Discipline.

export const REPORT_TYPES = [
  { id: 1, name: 'Academic', variant: 'blue' },
  { id: 2, name: 'Disciplinary', variant: 'amber' },
];
export const reportType = (id) => REPORT_TYPES.find((t) => t.id == id) ?? REPORT_TYPES[1];

// Where a report is in its review chain, as seen by the parent.
const STATUSES = [
  { name: 'Class adviser review', variant: 'amber' },
  { name: 'Prefect of Discipline review', variant: 'amber' },
  { name: 'Parent review', variant: 'red' },
];
export const reportStatus = (status) =>
  STATUSES[Number(status)] ?? { name: 'Resolved', variant: 'green' };

// An academic concern (report_type 1) is a follow-up: why the teacher is
// raising it, what they already tried, and what they recommend. The ids
// match the backend's lists (student-report-disciplinary.entity).
export const ACADEMIC_REASONS = [
  { value: 'no_improvement', title: 'No improvement in academic performance' },
  { value: 'absences_tardiness', title: 'Continued absences or tardiness' },
  { value: 'incomplete_requirements', title: 'Repeated incomplete requirements' },
  { value: 'poor_participation', title: 'Poor classroom participation' },
  { value: 'other', title: 'Other academic concern' },
];
export const PREVIOUS_INTERVENTIONS = [
  { value: 'remedial', title: 'Remedial activities / additional instruction' },
  { value: 'consultation', title: 'Individual student consultation' },
  { value: 'task_monitoring', title: 'Learning task reminders / monitoring' },
  { value: 'parent_coordination', title: 'Parent/guardian coordination' },
  { value: 'other', title: 'Other intervention' },
];
export const RECOMMENDED_ACTIONS = [
  { value: 'parent_conference', title: 'Parent/guardian conference' },
  { value: 'intervention_plan', title: 'Additional academic intervention plan' },
  { value: 'guidance_referral', title: 'Guidance office referral' },
  { value: 'intensified_monitoring', title: 'Intensified monitoring by Adviser' },
  { value: 'other', title: 'Other appropriate intervention' },
];

// A disciplinary concern (report_type 2): the kind of offense and what the
// teacher did on the spot. The ids match the backend's lists
// (student-report-disciplinary.entity); the kinds follow the severity
// examples below, but the Prefect still sets the severity.
export const OFFENSE_CATEGORIES = [
  { value: 'tardiness_cutting', title: 'Tardiness or cutting classes' },
  { value: 'uniform_grooming', title: 'Improper uniform or grooming' },
  { value: 'class_disruption', title: 'Disrupting class' },
  { value: 'disrespect', title: 'Disrespect toward teachers or staff' },
  { value: 'cheating', title: 'Cheating or academic dishonesty' },
  { value: 'bullying', title: 'Bullying or harassment' },
  { value: 'fighting', title: 'Fighting or physical harm' },
  { value: 'vandalism', title: 'Vandalism or damage to property' },
  { value: 'prohibited_items', title: 'Prohibited items or substances' },
  { value: 'theft', title: 'Theft' },
  { value: 'other', title: 'Other violation of school rules' },
];
export const TEACHER_ACTIONS = [
  { value: 'verbal_warning', title: 'Gave a verbal warning' },
  { value: 'talked_privately', title: 'Talked with the student privately' },
  { value: 'separated_students', title: 'Separated the students involved' },
  { value: 'confiscated_item', title: 'Confiscated the item' },
  { value: 'clinic_referral', title: 'Sent to the clinic' },
  { value: 'none', title: 'No action taken yet' },
  { value: 'other', title: 'Other action' },
];

// Files on a report (evidence when filing, a resolution file when
// resolving): one PDF, JPG or PNG of up to 1MB. The server checks the type
// and size too; this only says so before uploading.
export const ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
const ATTACHMENT_MAX = 1024 * 1024;
const ATTACHMENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
// v-file-input gives a File or a list of them, depending on the version.
export const fileOf = (v) => (Array.isArray(v) ? v[0] : v) || null;
export function attachmentProblem(file) {
  if (!file) return '';
  if (!ATTACHMENT_TYPES.includes(file.type)) return 'Attach a PDF, JPG or PNG file.';
  if (file.size > ATTACHMENT_MAX) return 'The file must be 1MB or smaller.';
  return '';
}
export const attachmentRule = (v) => attachmentProblem(fileOf(v)) || true;

// Whether the student has done what the Prefect's disciplinary action asks.
// The Prefect picks one of the first three; a case reads as overdue (from
// the server) once its due date has passed without being completed.
export const COMPLIANCE_STATUSES = [
  { value: 'pending', title: 'Pending', variant: 'secondary' },
  { value: 'in_progress', title: 'In Progress', variant: 'blue' },
  { value: 'completed', title: 'Completed', variant: 'green' },
];
const OVERDUE = { value: 'overdue', title: 'Overdue', variant: 'red' };
export const complianceStatus = (value) =>
  value === OVERDUE.value ? OVERDUE : (COMPLIANCE_STATUSES.find((c) => c.value === value) ?? null);

// The student's monitoring status when an academic concern is filed, set by
// the system from their attendance (LARDO) and grade (at risk) alerts.
export const MONITORING_STATUSES = {
  lardo: { name: 'LARDO', variant: 'red' },
  at_risk: { name: 'At risk', variant: 'amber' },
  none: { name: 'No alert', variant: 'secondary' },
};

const titleOf = (list, value) => list.find((o) => o.value === value)?.title ?? null;

// The Prefect's disciplinary action on a case and its follow-up, for
// <UiDetailList>: the action, compliance, due date and remarks.
export function caseActionDetails(report) {
  if (!report) return [];
  const compliance = complianceStatus(report.compliance);
  return [
    ...(report.disciplinary_action_name ? [{ label: 'Disciplinary action', value: report.disciplinary_action_name, span: 2 }] : []),
    ...(compliance ? [{ label: 'Compliance', value: compliance.title }] : []),
    ...(report.compliance_due_date ? [{ label: 'Due date', value: formatDate(report.compliance_due_date) }] : []),
    ...(report.action_taken ? [{ label: 'Remarks / action taken', value: report.action_taken, span: 2 }] : []),
  ];
}

// Label/value pairs for <UiDetailList>.
export function reportDetails(report) {
  if (!report) return [];
  const academic = Number(report.report_type) === 1;
  const monitoring = MONITORING_STATUSES[report.monitoring_status];
  return [
    { label: 'Reported by', value: report.teacher_name },
    { label: 'Filed on', value: formatDate(report.created_at) },
    { label: 'Subject', value: report.subject_title },
    { label: 'Grade level', value: report.grade_level },
    // Academic concerns from before the follow-up form have none of these.
    ...(academic && report.academic_reason
      ? [
          {
            label: 'Monitoring status',
            value: monitoring ? [monitoring.name, report.monitoring_detail].filter(Boolean).join(' · ') : '—',
          },
          { label: 'Reason for follow-up', value: titleOf(ACADEMIC_REASONS, report.academic_reason) },
          { label: 'Previous intervention', value: titleOf(PREVIOUS_INTERVENTIONS, report.previous_intervention) },
          { label: 'Recommended action', value: titleOf(RECOMMENDED_ACTIONS, report.recommended_action) },
        ]
      : [
          { label: 'Date of incident', value: formatDate(report.report_date) },
          { label: 'Time of incident', value: formatTime(report.report_time) },
          // Disciplinary concerns from before the incident form have none
          // of these.
          ...(!academic && report.offense_category
            ? [
                { label: 'Offense', value: titleOf(OFFENSE_CATEGORIES, report.offense_category) },
                { label: 'Where', value: report.incident_location || '—' },
                { label: 'Action taken by the teacher', value: titleOf(TEACHER_ACTIONS, report.teacher_action) ?? '—' },
                { label: 'Parents already told', value: Number(report.parent_informed) ? 'Yes' : 'No' },
                { label: 'Witnesses', value: report.witnesses || 'None named', span: 2 },
              ]
            : []),
        ]),
    {
      label: academic && report.academic_reason ? 'Progress observation' : 'Description',
      value: report.report_description || 'No description provided.',
      span: 2,
    },
  ];
}

// How serious a case is, set by the Prefect of Discipline once a report
// reaches them. The descriptions are what the "?" help shows; keep them in
// line with the school's student handbook.
export const REPORT_SEVERITIES = [
  {
    id: 'minor',
    name: 'Minor',
    variant: 'green',
    description: 'Improper behavior that does not hurt anyone or damage property. Usually settled with a warning or counseling.',
    examples: 'Tardiness, not wearing the proper uniform, disrupting class, littering.',
  },
  {
    id: 'major',
    name: 'Major',
    variant: 'amber',
    description: 'Deliberate or repeated misconduct that affects other students, staff or school property.',
    examples: 'Cheating, bullying, vandalism, disrespect toward teachers, repeating a minor offense.',
  },
  {
    id: 'severe',
    name: 'Severe',
    variant: 'red',
    description: "Acts that put someone's safety at risk or break the law. May call for the parents and outside authorities.",
    examples: 'Fighting that causes injury, bringing weapons or prohibited substances, theft, threats or harassment.',
  },
];
export const reportSeverity = (id) => REPORT_SEVERITIES.find((s) => s.id === id) ?? null;
